import type {NodeKey} from 'lexical';
import {
    DOMConversionMap,
    EditorConfig,
    LexicalNode,
    SerializedTextNode,
    Spread,
    TextNode
} from 'lexical';

type FontStyle = {
    size?: string;
    color?: string;
    family?: string;
};

export const nodeType = 'styled-text';

type SerializedFontNode = Spread<
    {
        fontStyles: FontStyle;
    },
    SerializedTextNode
>;

export class StyledTextNode extends TextNode {
    __nodeStyles: FontStyle;
    __fontStyles: FontStyle;

    constructor(text: string, style: FontStyle = {}, key?: NodeKey) {
        super(text, key);
        this.__nodeStyles = style;
    }

    isInline(): true {
        return true;
    }

    static getType(): string {
        return nodeType;
    }

    static clone(node: StyledTextNode): StyledTextNode {
        return new StyledTextNode(node.__text, node.__fontStyles, node.__key);
    }

    static importJSON(serializedNode: SerializedFontNode): StyledTextNode {
        return $createStyledTextNode(serializedNode.text, serializedNode.fontStyles);
    }

    static importDOM(): DOMConversionMap | null {
        return {
            span: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            },
            strong: (node: Node) => {
                return {
                    conversion: element => $styleNodeConversion(node, element),
                    priority: 1
                };
            }
        };
    }

    exportJSON(): SerializedFontNode {
        return {
            detail: this.getDetail(),
            format: this.getFormat(),
            mode: this.getMode(),
            style: this.getStyle(),
            text: this.getTextContent(),
            fontStyles: this.getFontStyles(),
            type: nodeType,
            version: 1
        };
    }

    getFontStyles(): FontStyle {
        const self = this.getLatest();
        return self.__nodeStyles;
    }

    createDOM(config: EditorConfig): HTMLElement {
        const element = super.createDOM(config);
        const {color, family, size} = this.__nodeStyles;
        if (color) element.style.color = color;
        if (family) element.style.fontFamily = family;
        if (size) element.style.fontSize = size;

        return element;
    }

    updateDOM(prevNode: StyledTextNode, dom: HTMLElement, config: EditorConfig): boolean {
        const isUpdated = super.updateDOM(prevNode, dom, config);
        if (prevNode.__nodeStyles !== this.__nodeStyles) {
            dom.style.color = this.__nodeStyles.color || '';
            dom.style.fontFamily = this.__nodeStyles.family || '';
            dom.style.fontSize = this.__nodeStyles.size || '';
        }
        return isUpdated;
    }
}

export const $styleNodeConversion = (node, element) => {
    const styles = element.style;
    const decorationStyles = {
        'font-size': styles.fontSize,
        'font-family': styles.fontFamily,
        color: styles.color
    };
    return {
        node: $createStyledTextNode(element.textContent || '', decorationStyles)
    };
};

export function $createStyledTextNode(text: string, styles: FontStyle): any {
    const node = new TextNode(text);

    const stringStyles = Object.entries(styles)
        .filter(([key, value]: [string, string]) => value)
        .map(([key, value]: [string, string]) => `${key}: ${value};`)
        .join(' ');
    node.setStyle(stringStyles);
    return node;
}

export function $isStyledTextNode(node?: LexicalNode): boolean {
    return node instanceof StyledTextNode;
}
